import React from "react";
import {
  Button,
  Container,
  Badge,
  Row,
  Col,
  Form,
  Modal,
} from "react-bootstrap";
import { connect } from "react-redux";

import AppComponent from "../../../Components/AppComponent";
import ItemList from "../../Components/ItemList";
import NavBar from "../../Components/NavBar";

import api from "../../../Services/Api/api";

import { getFullName } from "../../../Modules/utils/fullname";
import { dtFrmtS1 } from "../../../Modules/utils/etc";
import RenderAmount from "../../../Components/etc/RenderAmount";
import DynamicFilters from "../../MainScreen/TransactionsScreen/DynamicFilters";
import DateRangeFilter from "../../Components/DateRangeFilter";

/**
 * IMPORTANT:
 * Reuse the SAME date range filter component already used in Transactions
 */

const SCREEN_NAME = "TRANSACTION_LOGS_SCREEN";

/* ---------------------------------------------
 * Column Configuration
 * ------------------------------------------- */

const COLUMN_CONFIG = [
  {
    value: "sl",
    label: "Sl",
    render: ({ index }) => index + 1,
  },
  {
    value: "timestamp",
    label: "Date",
    render: ({ item }) => dtFrmtS1(item.timestamp),
  },
  {
    value: "action",
    label: "Action",
    render: ({ item }) => {
      const map = {
        create: { text: "Created", bg: "success" },
        update: { text: "Updated", bg: "warning" },
        delete: { text: "Deleted", bg: "danger" },
        restore: { text: "Restored", bg: "info" },
      };
      const v = map[item.action] || {
        text: item.action,
        bg: "secondary",
      };
      return <Badge bg={v.bg}>{v.text}</Badge>;
    },
  },
  {
    value: "actor",
    label: "Performed By",
    render: ({ item }) => getFullName(item.actor),
  },
  {
    value: "office",
    label: "Office",
    render: ({ item }) => getFullName(item.office),
  },
  {
    value: "relatedUser",
    label: "Related User",
    render: ({ item }) => (
      <span className="text-muted mono">
        {(item.before || item.after)?.relatedUser?.toString().slice(-6)}
      </span>
    ),
  },
  {
    value: "TrType",
    label: "Txn Type",
    render: ({ item }) => (item.before || item.after)?.transactionType,
  },
  {
    value: "txnId",
    label: "Txn Id",
    render: ({ item }) => (
      <span className="text-muted mono">
        {(item.before || item.after)?._id?.toString().slice(-6)}
      </span>
    ),
  },
  {
    value: "txn",
    label: "Txn Ref",
    render: ({ item }) => (
      <span className="text-muted mono">
        {item.transactionId?.toString().slice(-6)}
      </span>
    ),
  },
  {
    value: "rebuildFromEditId",
    label: "Edit Id",
    render: ({ item }) => (
      <span className="text-muted mono">
        {item.rebuildFromEditId?.toString().slice(-6)}
      </span>
    ),
  },

  {
    value: "summary",
    label: "Summary",
    render: ({ item }) => item.message || deriveLogMessage(item),
  },
  {
    value: "amountDelta",
    label: "Amount Δ",
    render: ({ item }) => (
      <RenderAmount
        value={item.delta?.totalAmount}
        style={{
          color:
            item.delta?.totalAmount > 0
              ? "green"
              : item.delta?.totalAmount < 0
                ? "red"
                : "black",
        }}
      />
    ),
  },
  {
    value: "officeBalanceAfter",
    label: "Office Balance",
    render: ({ item }) => renderBalance(item.balanceAfter?.totalAvailable),
  },
  {
    value: "details",
    label: "Details",
    render: ({ item, onRowClick }) => (
      <Button
        size="sm"
        variant="outline-secondary"
        onClick={(e) => {
          e.stopPropagation();
          onRowClick({ item });
        }}
      >
        View
      </Button>
    ),
  },
];

/* ---------------------------------------------
 * Helpers
 * ------------------------------------------- */

function deriveLogMessage(log) {
  const base =
    {
      create: "Transaction created",
      update: "Transaction updated",
      delete: "Transaction deleted",
      restore: "Transaction restored",
    }[log.action] || "Transaction changed";

  if (log.delta?.totalAmount) {
    const sign = log.delta.totalAmount > 0 ? "+" : "";
    return `${base} (Δ ${sign}${log.delta.totalAmount})`;
  }
  return base;
}

function renderBalance(value) {
  if (value === undefined || value === null)
    return <span className="text-muted">—</span>;
  return <RenderAmount value={value} />;
}

/* ---------------------------------------------
 * Log Details Modal
 * ------------------------------------------- */

function TransactionLogDetails({ show, onHide, log }) {
  if (!log) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title>Transaction Log Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-3">
          <Col md={4}>
            <strong>Action:</strong> {log.action}
          </Col>
          <Col md={4}>
            <strong>Performed By:</strong> {getFullName(log.actor)}
          </Col>
          <Col md={4}>
            <strong>Date:</strong> {dtFrmtS1(log.timestamp)}
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <h6>Before</h6>
            <pre className="bg-light p-2 border">
              {JSON.stringify(log.before, null, 2)}
            </pre>
          </Col>
          <Col md={6}>
            <h6>After</h6>
            <pre className="bg-light p-2 border">
              {JSON.stringify(log.after, null, 2)}
            </pre>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ---------------------------------------------
 * Screen
 * ------------------------------------------- */

class TransactionLogsScreenHeadOffice extends AppComponent {
  state = {
    logs: [],
    offices: [],
    triggerReload: 0,
    filterType: "today",
    filters: {
      office: undefined,
      timestamp: undefined, // reused by DynamicFilters
    },
    visibleModal: false,
    selectedLog: null,
  };

  componentDidMount() {
    this.onMount();
    this.loadOffices();
  }

  async loadOffices() {
    const res = await api.get("v1/head-office/user", {
      queryStr: JSON.stringify({
        where: { role: "office" },
        limit: 0,
      }),
    });
    this.setAsyncState({ offices: [this.props.user, ...(res?.users || [])] });
  }

  async retriveData(query) {
    const res = await api.get("v1/office/transaction-log", query);
    return Array.isArray(res?.transactionLogs) ? res.transactionLogs : [];
  }

  async setItems(items) {
    this.setAsyncState({ logs: items });
  }

  render() {
    const { triggerReload, offices, filters } = this.state;

    return (
      <>
        <NavBar activeLink="/transaction-logs" />

        <Container className="gainner">
          {/* Header */}
          <div className="pagetitlebar mb-3 d-flex justify-content-between">
            <div className="title">Transaction Logs</div>
            <Button
              variant="secondary"
              onClick={() =>
                this.setAsyncState({
                  triggerReload: Date.now(),
                })
              }
            >
              Reload
            </Button>
          </div>

          {/* Filters */}
          <div className="mb-3">
            <Row className="align-items-end">
              <Col md={4}>
                <Form.Select
                  value={filters.office || ""}
                  onChange={(e) =>
                    this.setAsyncState({
                      filters: {
                        ...filters,
                        office: e.target.value || undefined,
                      },
                      triggerReload: Date.now(),
                    })
                  }
                >
                  <option value="">All Offices</option>
                  {offices.map((o) => (
                    <option key={o._id} value={o._id}>
                      {getFullName(o)}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={8}>
                <div className="d-flex justify-content-end">
                  <div className="text-nowrap " style={{ maxWidth: "400px" }}>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "gray",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      Date Range Filter
                    </div>

                    <DateRangeFilter
                      filterType={this.state?.filterType}
                      startDate={filters?.timestamp?.$gte}
                      endDate={filters?.timestamp?.$lt}
                      maxDateCount={0}
                      onChange={({ filterType, start, end }) => {
                        this.setAsyncState({
                          triggerReload: Date.now(),
                          filterType,
                          filters: {
                            ...filters,
                            timestamp: {
                              $gte: start,
                              $lt: end,
                            },
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* List */}
          <ItemList
            defaultQuery={{
              populate: "actor office",
              limit: 50,
              order: -1,
            }}
            additionalQueries={{
              where: {
                ...(filters.office && {
                  office: filters.office,
                }),
                timestamp: filters.timestamp || {},
              },
            }}
            triggerReload={triggerReload}
            items={this.state.logs}
            table={{
              columns: COLUMN_CONFIG,
              onRowClick: ({ item }) =>
                this.setAsyncState({
                  visibleModal: true,
                  selectedLog: item,
                }),
            }}
            retriveData={this.retriveData.bind(this)}
            setItems={this.setItems.bind(this)}
          />

          {/* Details Modal */}
          <TransactionLogDetails
            show={this.state.visibleModal}
            log={this.state.selectedLog}
            onHide={() =>
              this.setAsyncState({
                visibleModal: false,
                selectedLog: null,
              })
            }
          />
        </Container>
      </>
    );
  }
}

/* ---------------------------------------------
 * Redux
 * ------------------------------------------- */

const mapStateToProps = (state) => ({
  user: state.pState.user.user,
});

export default connect(mapStateToProps)(TransactionLogsScreenHeadOffice);
